import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, Link2 } from 'lucide-react';
import { AccesoSocial } from '../components/AccesoSocial';
import { Logo } from '../components/Logo';
import { mensajeErrorAuth } from '../utils/authErrores';
import { NOMBRE_APP, LEMA_APP } from '../config/marca';

export const Login: React.FC = () => {
  const { login, recuperarContrasena, vinculacionPendiente, cancelarVinculacion, cuentaBorrada } = useAuth();
  // Si venía de «Continuar con Apple/Google» y ya tenía cuenta, su email ya se sabe
  const [email, setEmail] = useState(vinculacionPendiente?.email ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [aviso, setAviso] = useState('');
  const [enviandoRecuperacion, setEnviandoRecuperacion] = useState(false);
  const navigate = useNavigate();
  // Adónde volver al entrar: lo que pidió PrivateRoute, o el inicio
  const desde: string = (useLocation().state as { desde?: string } | null)?.desde ?? '/';
  const vieneDeInvitacion = desde.startsWith('/amedias/unirse/') || desde.startsWith('/tareas/unirse/');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate(desde, { replace: true });
    } catch (err: any) {
      setError(mensajeErrorAuth(err, 'Error al iniciar sesión'));
    } finally {
      setLoading(false);
    }
  };

  const handleRecuperar = async () => {
    setError(''); setAviso('');
    if (!email.trim()) {
      setError('Escribe tu email arriba y vuelve a pulsar «¿Has olvidado tu contraseña?».');
      return;
    }
    // Mismo mensaje exista o no la cuenta: no se dice quién está registrado
    const enviado = `Si hay una cuenta con contraseña para ${email.trim()}, te llegará un correo para poner una nueva. Mira también en correo no deseado.`;
    setEnviandoRecuperacion(true);
    try {
      await recuperarContrasena(email);
      setAviso(enviado);
    } catch (err: any) {
      if (err?.code === 'auth/user-not-found') {
        setAviso(enviado);
      } else {
        setError(mensajeErrorAuth(err, 'No se ha podido enviar el correo. Inténtalo más tarde.'));
      }
    } finally {
      setEnviandoRecuperacion(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-8">
        <div className="flex justify-center mb-8">
          <Logo size={64} sobreClaro />
        </div>

        <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">{NOMBRE_APP}</h1>
        <p className="text-center text-gray-600 mb-8">{LEMA_APP}</p>

        {vieneDeInvitacion && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg mb-6 text-sm">
            Inicia sesión o crea una cuenta para unirte {desde.startsWith('/tareas/') ? 'a la casa a la que' : 'al plan al que'} te han invitado.
          </div>
        )}

        {cuentaBorrada && (
          <div className="bg-gray-50 border border-gray-200 text-gray-700 px-4 py-3 rounded-lg mb-6 text-sm">
            Tu cuenta y tus datos se han borrado. Gracias por haber usado {NOMBRE_APP}.
          </div>
        )}

        {vinculacionPendiente && (
          <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg mb-6 text-sm">
            <div className="flex gap-2">
              <Link2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>
                Ya tienes una cuenta con <strong className="break-all">{vinculacionPendiente.email}</strong>.
                Entra como lo hacías hasta ahora (con tu contraseña o con el otro botón) y
                quedará vinculada a {vinculacionPendiente.proveedor === 'apple' ? 'Apple' : 'Google'}.
              </p>
            </div>
            <button type="button" onClick={cancelarVinculacion}
              className="text-xs font-semibold text-blue-700 hover:underline mt-2 ml-6">
              No vincular
            </button>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {aviso && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg mb-6 text-sm">
            {aviso}
          </div>
        )}

        <AccesoSocial desde={desde} onError={setError} />

        <form onSubmit={handleLogin} className="space-y-6 mt-4">
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="email"
                autoComplete="email" autoCapitalize="none" enterKeyHint="next"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="tu@email.com"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2">Contraseña</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="password"
                autoComplete="current-password" enterKeyHint="go"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="••••••••"
                required
              />
            </div>
            <button type="button" onClick={handleRecuperar} disabled={enviandoRecuperacion}
              className="text-sm text-blue-600 hover:underline mt-2 py-1 disabled:opacity-50">
              {enviandoRecuperacion ? 'Enviando…' : '¿Has olvidado tu contraseña?'}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white font-semibold py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
          >
            {loading ? 'Iniciando...' : 'Iniciar Sesión'}
          </button>
        </form>

        <p className="text-center text-gray-600 mt-6">
          ¿No tienes cuenta?{' '}
          <button
            onClick={() => navigate('/signup', { state: { desde } })}
            className="text-blue-600 font-semibold hover:underline"
          >
            Regístrate aquí
          </button>
        </p>
        <p className="text-center text-sm mt-3">
          <a href="/ayuda" className="text-gray-500 hover:underline">¿Qué es esto? Ver la ayuda</a>
          <span className="text-gray-300 mx-2">·</span>
          <a href="/privacidad" className="text-gray-500 hover:underline">Privacidad</a>
        </p>
      </div>
    </div>
  );
};
